import { useDroppable } from "@dnd-kit/core";
import { PlusIcon } from "@heroicons/react/24/solid";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/core/components/ui/button";
import { Card } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { sortDateDescending } from "@/core/utils/date";
import { cn } from "@/core/utils/utils";
import AddReferralModal from "@/referrals/common/add-referral-modal";
import {
  REFERRAL_COLUMN,
  REFERRAL_COLUMN_TO_HEADER,
} from "@/referrals/constants";
import type {
  IReferral,
  ReferralColumn as ReferralColumnType,
} from "@/referrals/referral";
import ReferralCard from "@/referrals/referral-kanban-view/referral-card/referral-card";
import { ReferralCardSkeleton } from "@/referrals/referral-kanban-view/referral-card/referral-card";
import NumberOfReferrals from "./number-of-referrals";

interface ReferralColumnProps {
  column: ReferralColumnType;
  referralsMap: { [key: string]: IReferral[] };
  isLoading?: boolean;
  activeColumn?: ReferralColumnType;
  activeId?: string | null;
  selectedReferralId?: string;
  onCardClick: (referral: IReferral) => void;
  onNextColumn: (column: ReferralColumnType, referral: IReferral) => void;
  onPayout: (referral: IReferral) => void;
  onLost?: (referral: IReferral) => void;
  onMoveToPaid?: (referral: IReferral) => void;
  onDelete?: (referral: IReferral) => void;
}

const COLUMN_TO_SKELETON_NUMBER = {
  [REFERRAL_COLUMN.inProgress]: 3,
  [REFERRAL_COLUMN.scheduled]: 2,
  [REFERRAL_COLUMN.closed]: 3,
  [REFERRAL_COLUMN.new]: 5,
} as const;

const ReferralColumn = ({
  column,
  referralsMap,
  isLoading = false,
  activeColumn,
  activeId,
  selectedReferralId,
  onCardClick,
  onNextColumn,
  onPayout,
  onLost,
  onMoveToPaid,
  onDelete,
}: ReferralColumnProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddReferralModalOpen, setIsAddReferralModalOpen] = useState(false);

  const { setNodeRef, isOver } = useDroppable({
    id: column,
  });

  const showBorder = isOver && activeColumn !== column;

  const referrals = useMemo(
    () =>
      [...(referralsMap[column] || [])].sort((a, b) =>
        sortDateDescending(a.created_at, b.created_at),
      ),
    [referralsMap, column],
  );

  return (
    <Stack
      ref={setNodeRef}
      className={cn(
        "relative min-h-72 w-[266px] min-w-[266px] gap-4 rounded-b-2xl rounded-t-lg border-2 border-transparent",
        showBorder && "border-2 border-stroke-interactive",
      )}
    >
      <Card className="sticky top-0 z-10 w-full rounded-lg">
        <Stack
          direction="row"
          justify="between"
          align="center"
          className="min-h-12 pl-4"
          fullWidth
        >
          <Stack direction="row" align="center" gap="sm">
            <Typography variant="subtitleW3">
              {REFERRAL_COLUMN_TO_HEADER[column]}
            </Typography>
            <NumberOfReferrals
              count={isLoading ? 0 : referrals.length}
              column={column}
            />
          </Stack>
          {column === REFERRAL_COLUMN.inProgress && (
            <AddReferralModal
              // TODO: Change when a single user can view multiple main referral screens
              //No businessId set, will default to user's businessId
              open={isAddReferralModalOpen}
              onOpenChange={setIsAddReferralModalOpen}
              onSuccess={(data) => {
                if (selectedReferralId !== data.id) {
                  searchParams.set("selected", data.id);
                  setSearchParams(searchParams);
                }
              }}
              trigger={
                <Button
                  variant="icon"
                  size="icon"
                  data-cy="add-referral-button"
                >
                  <PlusIcon className="h-4 w-4 text-tertiary-text" />
                </Button>
              }
            />
          )}
        </Stack>
      </Card>

      <Stack fullWidth>
        {isLoading
          ? Array.from({ length: COLUMN_TO_SKELETON_NUMBER[column] }).map(
              (_, index) => (
                <ReferralCardSkeleton
                  key={`loading-${index}`}
                  column={column}
                />
              ),
            )
          : referrals.map((referral) => (
              <ReferralCard
                key={referral.id}
                referral={referral}
                column={column}
                isDragging={activeId === referral.id}
                isSelected={selectedReferralId === referral.id}
                onClick={() => onCardClick(referral)}
                onNextColumn={(column) => onNextColumn(column, referral)}
                onPayout={() => onPayout(referral)}
                onLost={() => onLost?.(referral)}
                onMoveToPaid={() => onMoveToPaid?.(referral)}
                onDelete={() => onDelete?.(referral)}
              />
            ))}
      </Stack>
    </Stack>
  );
};

export default ReferralColumn;
